import React from "react";
import { Box, Typography } from "@mui/material";

/**
 * Skeuomorphic sliding toggle for Student / Parent portal switch.
 * Capsule track with labels and a circular knob that slides left (Student) or right (Parent).
 */
const PortalToggleSwitch = ({
  value,
  onSelectStudent,
  onSelectParent,
  disabled = false,
  size = "medium",
}) => {
  const isStudent = value === "student";

  const trackHeight = size === "small" ? 28 : 36;
  const knobSize = size === "small" ? 24 : 32;
  const trackWidth = size === "small" ? 100 : 120;
  const knobOffset = 2;
  const travel = trackWidth - knobSize - knobOffset * 2;

  const handleTrackClick = (e) => {
    if (disabled) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const mid = rect.width / 2;
    if (x < mid) {
      onSelectStudent?.();
    } else {
      onSelectParent?.();
    }
  };

  return (
    <Box
      role="switch"
      aria-checked={!isStudent}
      aria-label={`Portal: ${isStudent ? "Student" : "Parent"}`}
      onClick={handleTrackClick}
      sx={{
        position: "relative",
        width: trackWidth,
        height: trackHeight,
        borderRadius: trackHeight / 2,
        background: "linear-gradient(180deg, #3a3a3a 0%, #1a1a1a 50%, #0d0d0d 100%)",
        boxShadow: "inset 0 2px 4px rgba(0,0,0,0.4), 0 2px 6px rgba(0,0,0,0.35)",
        border: "1px solid rgba(255,255,255,0.08)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      {/* Single label: show only the "switch to" option (Parent when in Student, Student when in Parent) */}
      <Typography
        variant="caption"
        sx={{
          position: "absolute",
          ...(isStudent
            ? { right: 10, left: "auto" }
            : { left: 10, right: "auto" }),
          top: "50%",
          transform: "translateY(-50%)",
          color: "rgba(255,255,255,0.95)",
          fontWeight: 600,
          fontSize: size === "small" ? "0.65rem" : "0.7rem",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        {isStudent ? "Parent" : "Student"}
      </Typography>

      {/* Sliding knob - brushed metal style */}
      <Box
        sx={{
          position: "absolute",
          top: knobOffset,
          left: knobOffset,
          width: knobSize,
          height: knobSize,
          borderRadius: "50%",
          transform: `translateX(${isStudent ? 0 : travel}px)`,
          transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          zIndex: 1,
          background: `
            radial-gradient(ellipse 70% 60% at 35% 35%, rgba(255,255,255,0.5) 0%, transparent 50%),
            radial-gradient(ellipse 50% 50% at 50% 50%, rgba(200,200,200,0.25) 0%, transparent 60%),
            linear-gradient(145deg, #e8e8e8 0%, #a0a0a0 30%, #6b6b6b 60%, #4a4a4a 100%)
          `,
          boxShadow: `
            inset 0 1px 0 rgba(255,255,255,0.4),
            inset 0 -1px 0 rgba(0,0,0,0.2),
            0 2px 4px rgba(0,0,0,0.3),
            0 1px 0 rgba(255,255,255,0.1)
          `,
          border: "1px solid rgba(255,255,255,0.15)",
        }}
      />
    </Box>
  );
};

export default PortalToggleSwitch;
